import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import {
  CapacityPoolDraft,
  DEFAULT_POOL_STARVATION_MINUTES,
  newPoolDraft,
} from './capacityPools';

interface CapacityPoolsFieldsProps {
  pools: CapacityPoolDraft[];
  setPools: (pools: CapacityPoolDraft[]) => void;
  starvationMinutes: string;
  setStarvationMinutes: (value: string) => void;
}

/**
 * Editor for a provider's capacity pools: reserved blocks of one GPU type that
 * launches wait for, because the provider cannot tell us how much of the block
 * is in use.
 */
export default function CapacityPoolsFields({
  pools,
  setPools,
  starvationMinutes,
  setStarvationMinutes,
}: CapacityPoolsFieldsProps) {
  const updateRow = (id: string, patch: Partial<CapacityPoolDraft>) => {
    setPools(pools.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  return (
    // A Box, not a FormControl: every row's Input would take the same id from it.
    <Box sx={{ mt: 2 }}>
      <Typography level="title-sm">Capacity pools</Typography>
      <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1 }}>
        For GPUs you have reserved a fixed number of, e.g. 8 H100s. A launch on
        this provider that asks for a pooled GPU waits (status WAITING) until
        the pool has room; every other launch goes straight through. A pool also
        covers variants of its card, so H100 includes H100-SXM.
      </Typography>

      <Stack spacing={1}>
        {pools.map((row) => (
          <Stack key={row.id} direction="row" spacing={1} alignItems="center">
            <Input
              value={row.accelerator}
              onChange={(event) =>
                updateRow(row.id, { accelerator: event.currentTarget.value })
              }
              placeholder="Accelerator, e.g. H100"
              sx={{ flex: 2, minWidth: 0 }}
              slotProps={{ input: { 'aria-label': 'Pool accelerator' } }}
            />
            <Input
              value={row.maxGpus}
              onChange={(event) =>
                updateRow(row.id, { maxGpus: event.currentTarget.value })
              }
              type="number"
              placeholder="GPUs"
              endDecorator="GPUs"
              sx={{ flex: 1, minWidth: 0 }}
              slotProps={{
                input: { min: 1, step: 1, 'aria-label': 'Pool size in GPUs' },
              }}
            />
            <IconButton
              variant="plain"
              color="danger"
              onClick={() => setPools(pools.filter((r) => r.id !== row.id))}
              aria-label={`Remove the ${row.accelerator || 'empty'} pool`}
            >
              <Trash2Icon size={16} />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Box>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<PlusIcon size={14} />}
          onClick={() => setPools([...pools, newPoolDraft()])}
          sx={{ mt: pools.length ? 1 : 0 }}
        >
          Add pool
        </Button>
      </Box>

      {pools.length > 0 && (
        <FormControl sx={{ mt: 1.5 }}>
          <FormLabel>Starvation guard (minutes)</FormLabel>
          <Input
            value={starvationMinutes}
            onChange={(event) =>
              setStarvationMinutes(event.currentTarget.value)
            }
            type="number"
            placeholder={String(DEFAULT_POOL_STARVATION_MINUTES)}
            sx={{ maxWidth: 200 }}
            slotProps={{ input: { min: 0, step: 1 } }}
          />
          <FormHelperText>
            Smaller jobs may start ahead of a bigger one that does not fit yet.
            Once the bigger one has waited this long, nothing else starts in its
            pool until it has launched. 0 turns the guard off. Blank means{' '}
            {DEFAULT_POOL_STARVATION_MINUTES}.
          </FormHelperText>
        </FormControl>
      )}
    </Box>
  );
}
